import { destroy } from 'redux-form';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
  loadAlumnoSuccess,
  loadCatalogoSuccess,
  LOAD_ALUMNO,
  LOAD_CATALOGO,
  SAVE_FILE,
  LOAD_DATA_NUCLEO,
  loadDataNucleoSuccess,
  setStudentAttribute,
  DESTROY_MATRICULAR_FORM,
  PROCESAR_MATIRCULA,
  procesarMatriculaSuccess,
  SAVE_DRAFT,
  saveDraftSuccess,
} from '../../redux/actions/matricularActions';
import {
  ROUTE_ALUMNO,
  ROUTE_CATALOGO,
  ROUTE_NUCLEO,
  ROUTE_MATRICULA_DAY_CARE,
  ROUTE_NOTIFICACION,
  programaToCorreo,
} from '../../utils/constants';
import toAPIPath from '../../utils/locationResolver';
import { uploadFile, saveAlumno } from '../../utils/apiHelpers';
import { buildEnrollmentInProgressInternalNotificationEmail, buildEnrollmentInProgressNotificationEmail } from '../../utils/emailTemplates';

export function* getNucleoData({ api, data }) {
  try {
    const filter = encodeURIComponent(`{"include": [{"relation":"telefono"},{"relation":"direccion"},{"relation":"medico"},{"relation":"encargado"},{"relation":"alumno"}]}`);
    const nucleoData = yield call(api.get, toAPIPath([ROUTE_NUCLEO, `${data.nucleo}?filter=${filter}&access_token=${data.token}`]));
    yield put(loadDataNucleoSuccess(nucleoData));
  } catch (err) {
    throw err;
  }
}

export function* getCatalogo({ api, token }) {
  try {
    const catalogo = yield call(api.get, toAPIPath([ROUTE_CATALOGO, `findOne?access_token=${token}`]));
    yield put(loadCatalogoSuccess(catalogo));
  } catch (err) {
    throw err;
  }
}

export function* getAlumno({ api, data }) {
  try {
    const alumno = yield call(api.get, toAPIPath([ROUTE_ALUMNO, `${data.id}?access_token=${data.token}`]));

    const attributes = [
      'direccion', 'contactoEmergencia', 'medicoCabecera', 'bitacora',
      'telefono', 'encargado', 'alergiasAlimenticias', 'intoleranciasAlimenticias',
      'problemasGastrointestinales', 'padecimientos', 'medicamentos', 'antecedentes',
      'alergiasMedicamentos', 'limitacionesFisicas', 'dietaEspecial',
    ];

    yield all(attributes.map(attr =>
      put(setStudentAttribute({ attribute: attr, value: alumno[attr] }, true))
    ));

    yield call(getNucleoData, { api, data: { token: data.token, nucleo: alumno.nucleo } });
    yield put(loadAlumnoSuccess(alumno));
  } catch (err) {
    throw err;
  }
}

export function* postMatricula({ api, data }) {
  const { alumno, dietaEspecialForm, matriculaDayCare, token, isNew, nucleo, filesForms, catalogo } = data;
  try {
    if (alumno.dietaEspecial && alumno.dietaEspecial.name && dietaEspecialForm && dietaEspecialForm.file) {
      yield call(uploadFile, { api, token, name: dietaEspecialForm.name, file: dietaEspecialForm.file });
    }

    yield all(filesForms.map(f =>
      call(uploadFile, { api, token, name: f.form.name, file: f.form.file, type: f.type })
    ));

    if (nucleo) {
      yield call(api.patch, toAPIPath([ROUTE_NUCLEO, `${alumno.nucleo}?access_token=${token}`]), nucleo);
    }

    let alumnoId = alumno.id;
    const savedAlumno = yield call(saveAlumno, { api, alumno, token, isNew, alumnoId });
    alumnoId = savedAlumno.id;

    if (matriculaDayCare) {
      matriculaDayCare.alumno = alumnoId;
      yield call(api.post, toAPIPath([`${ROUTE_MATRICULA_DAY_CARE}?access_token=${token}`]), matriculaDayCare);
    }

    const nucleoData = yield call(api.get, toAPIPath([ROUTE_NUCLEO, `${alumno.nucleo}?filter=${encodeURIComponent('{"include": [{"relation":"encargado"}]}')}&access_token=${token}`]));
    const encargadosEmails = nucleoData.encargado.map(e => e.email).join(',');

    const appState = yield select(state => state.app);
    const usuario = appState.session.user.nombre;

    const emailToParents = buildEnrollmentInProgressNotificationEmail(alumno, encargadosEmails, usuario);
    yield call(api.post, toAPIPath([`${ROUTE_NOTIFICACION}?access_token=${token}`]), emailToParents);

    const programa = Object.keys(catalogo.programas).find(
      (p) => catalogo.programas[p].niveles.indexOf(alumno.nivel) > -1
    );

    const correoCoordinacion = programaToCorreo[programa] || "";

    const toEmails = `direccion.general@weizmancr.net, contabilidad@weizmancr.net, direccion.adm.fin@weizmancr.net, asistente.direccion@weizmancr.net, recepcion@weizmancr.net, asistente.contable@weizmancr.net, proveeduria@weizmancr.net, informatica@weizmancr.net, enfermeria@weizmancr.net, coordinacion.bienestar.escolar@weizmancr.net, comunicaciones@weizmancr.net, biblioteca@weizmancr.net${correoCoordinacion ? `, ${correoCoordinacion}` : ""}`;

    const emailToAdmin = buildEnrollmentInProgressInternalNotificationEmail(alumno, toEmails);
    yield call(api.post, toAPIPath([`${ROUTE_NOTIFICACION}?access_token=${token}`]), emailToAdmin);

    yield put(procesarMatriculaSuccess());
  } catch (err) {
    throw err;
  }
}

export function* postDraft({ api, data }) {
  const { alumno, dietaEspecialForm, token, isNew, nucleo, filesForms } = data;
  try {
    if (alumno.dietaEspecial && alumno.dietaEspecial.name && dietaEspecialForm && dietaEspecialForm.file) {
      yield call(uploadFile, { api, token, name: dietaEspecialForm.name, file: dietaEspecialForm.file });
    }

    yield all(filesForms.map(f =>
      call(uploadFile, { api, token, name: f.form.name, file: f.form.file, type: f.type })
    ));

    if (nucleo) {
      yield call(api.patch, toAPIPath([ROUTE_NUCLEO, `${alumno.nucleo}?access_token=${token}`]), nucleo);
    }

    let alumnoId = alumno.id;
    const savedAlumno = yield call(saveAlumno, { api, alumno, token, isNew, alumnoId });
    alumnoId = savedAlumno.id;

    yield put(saveDraftSuccess(alumnoId));
  } catch (err) {
    throw err;
  }
}

export function* destroyForm({ form }) {
  try {
    yield put(destroy(form));
  } catch (err) {
    console.error('Error destroying form:', err);
  }
}

export default function* rootSaga() {
  yield takeLatest(LOAD_CATALOGO, getCatalogo);
  yield takeLatest(LOAD_ALUMNO, getAlumno);
  yield takeLatest(LOAD_DATA_NUCLEO, getNucleoData);
  yield takeLatest(SAVE_FILE, uploadFile); // uses refactored uploadFile directly
  yield takeLatest(DESTROY_MATRICULAR_FORM, destroyForm);
  yield takeLatest(PROCESAR_MATIRCULA, postMatricula);
  yield takeLatest(SAVE_DRAFT, postDraft);
}
